import {DeviceConnectProvide} from "@hybird/core/device/connect";
import {DeviceBeginSealType, DeviceEvent} from "@hybird/core/device/protocol";
import EventEmitter from "eventemitter3";
import {MqttDeviceProtocol, MqttSend} from "./mqtt";
import {Buffer} from "buffer";
import {config, DeviceType} from "../config";
import {WebError} from "@hybird/core/error";
import {deviceError, DeviceResultError} from "@hybird/device/deviceError";
import service from "@hybird/core/request";
import url from "@hybird/device/request";
import request from "@hybird/device/request";
import "@hybird/core/lib"
import {
  DefaultDeviceProtocol,
  DefaultDeviceProtocolBeginSeal,
  DefaultDeviceProtocolOperation
} from "@hybird/device/core";

class BleCodec {
  static codec = new BleCodec();

  encode(action, params, {reply = false, errorCode}) {
    if (!reply) {
      const data = `|${action}|${(params && params.length > 0 ? params.join("~") : "~")}|#`;
      const msg = `/>|${data.length + 3 + data.length.toString().length}${data}`;
      return Buffer.from(msg);
    } else {
      let code = 0;
      let codeMsg = "OK"

      if (errorCode) {
        code = errorCode.code;
        codeMsg = "FAIL";
      }

      params.unshift(code);
      params.unshift(codeMsg);

      const data = `|${action}|${(params && params.length > 0 ? params.join("~") : "~")}|#`;
      const msg = `/>|${data.length + 3 + data.length.toString().length}${data}`;
      return Buffer.from(msg);
    }
  }

  /**
   *
   * @param data{String}
   * @return {{
   *     success: boolean,
   *     action: String,
   *     params: []
   * }}
   */
  decode(data) {
    const tmp = data.split("|");

    const msg = tmp[3] === "~" ? [] : tmp[3].split("~");

    return {
      success: !!msg[0] && msg[0] !== "NG" && msg[0] !== "FAIL",
      action: tmp[2],
      params: msg,
      payload: data,
      extra: tmp.length > 3 ? tmp[4] : undefined
    };
  }
}

class BleDevice extends EventEmitter {
  name;
  /**
   * @type {string[]}
   */
  serviceUUIDs;
  characteristicUUIDs;
  _connectStatus = true;
  _lastMessage = {};
  /**
   * @type{BleCodec}
   */
  codec;

  /**
   * 连接状态, 0: 初始化  1为连接中  2: 连接成功  3: 取消连接
   * @type {number}
   */
  connectStatus = 0;

  writeQueue = [];
  isWriting = false;

  constructor(name, {
    serviceUUIDs, characteristicUUIDs, bleCodec = BleCodec.codec
  }, config) {
    super();
    this.name = name;
    this.serviceUUIDs = serviceUUIDs;
    this.characteristicUUIDs = characteristicUUIDs;
    this.codec = bleCodec;
    this.config = config;
  }

  async connect({timeout}) {
    let clear;
    return new Promise((resolve, reject) => {
      clear = this._createSetTimeout(timeout, reject, deviceError.NOT_CONNECT_DEVICE);

      this.connectStatus = 1;
      this._connect({clear, resolve, reject});
    }).then(() => {
      clear?.call(this);
      this.connectStatus = 2;
      this._connectStatus = true;
    }).catch(e => {
      this.connectStatus = 3;
      this.disconnect();

      if (deviceError.isError(e, deviceError.FLUTTER_INIT_ERROR) && e.data?.code === 7014) {
        throw WebError.code(deviceError.CONNECT_ILLEGAL_ARGUMENT, {
          msg: e.data?.errMsg,
          error: e
        });
      } else if (deviceError.isError(e, deviceError.NOT_CONNECT_DEVICE)) {
        throw e;
      } else if (e instanceof WebError) {
        throw e;
      } else {
        throw WebError.code(deviceError.CONNECT_ILLEGAL_ARGUMENT, {error: e});
      }
    });
  }

  /**
   * @abstract
   * @param clear
   * @param resolve
   * @param reject
   * @private
   */
  _connect({clear, resolve, reject}) {

  }

  _createSetTimeout(timeout = 30, reject, message, msg) {
    let clearFn;

    const stop = setTimeout(async () => {
      reject(WebError.code(message));

      clearFn?.call(this);
    }, timeout * 1000);

    return function (fn) {
      if (typeof fn === "function") {
        clearFn = fn;
      } else {
        clearTimeout(stop);
      }
    };
  }

  async write({
                reply = false, errorCode, buffer, action, params, timeout = 1000
              }) {
    return new Promise((resolve, reject) => {
      if (!buffer) {
        buffer = this.codec.encode(action, params, {reply, errorCode});
      }

      console.log("publish", action, buffer.toString(), buffer.toString("hex"));

      this._lastMessage[action] = params;
      const clear = this._createSetTimeout(timeout, reject, deviceError.WRITE_TIMEOUT);
      //使用队列进行发送, 防止发送失败的情况
      this.writeQueue.push({clear, buffer, timeout, reject, resolve});
      this.processQueue(); // 触发队列处理
    });
  }

  async processQueue() {
    // 如果当前正在写入，或者队列为空，则直接返回
    if (this.isWriting || this.writeQueue.length === 0) {
      return;
    }

    this.isWriting = true;
    const {clear, buffer, timeout, reject, resolve} = this.writeQueue.shift(); // 取出队列第一个任务

    try {
      // 执行写入操作
      await this._write({clear, buffer, timeout, reject, resolve}).then(() => {
        clear();
      });
      await this.delay(50);
    } catch (error) {
      reject(error); // 失败则拒绝 Promise
    } finally {
      this.isWriting = false; // 无论成功与否，都标记为写入完成
      this.processQueue(); // 继续处理下一个任务
    }
  }

  // 简单的节流函数
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   *
   * @abstract
   * @param clear
   * @param buffer
   * @param timeout
   * @param resolve
   * @param reject
   * @return {Promise<void>}
   * @private
   */
  async _write({clear, buffer, timeout, resolve, reject}) {

  }

  async _onMessage(result) {
    const parseResult = this.codec.decode(result);

    if (!this.isAll(this._lastMessage[parseResult.action], parseResult.params)) {
      await this.emitMessage(parseResult);
    } else {
      console.error("ble", "获取到异常数据", JSON.stringify(parseResult));
    }
  }

  async emitMessage(parseResult) {
    const fns = this.listeners("read");

    for (const i in fns) {
      const event = fns[i];
      await event.call(this, parseResult);
    }
  }


  // 使用for 循环
  isAll(array, array1) {
    if (array && array.length === array1.length) {
      for (let index = 0; index < array.length; index++) {
        if (array[index] != array1[index]) {
          return false
        }

      }
      return true;
    }

    return false;
  }

  get state() {
    return "disconnect";
  }

  get connected() {
    return false;
  }

  async disconnect() {
    this._connectStatus = false;
    await this._disconnect();
    this.connectStatus = 3;
  }

  async _disconnect() {
  }
}

class BleDeviceConnectProvide extends DeviceConnectProvide {
  _connectForce = true;

  /**
   * @type BleDevice
   */
  bleDevice;

  /**
   * @type{DeviceConnectProvide}
   */
  networkConnectProvide;

  _onDisconnect;


  /**
   *
   * @param createBleDevice{{create:function(Config):BleDevice, isSupport: function():boolean, canConnect(Config, BleDevice)}}
   * @param networkConnectProvide
   */
  constructor(createBleDevice, networkConnectProvide) {
    super();
    this._createBleDevice = createBleDevice;
    this.networkConnectProvide = networkConnectProvide;
  }

  set codec(codec) {
    if (codec instanceof BleCodec && this.bleDevice) {
      this.bleDevice.codec = codec;
    }
  }

  get connectType() {
    return 1;
  }

  isSupport(connectType, config) {
    return this._createBleDevice.isSupport() && connectType === this.connectType;
  }

  async connect({
                  connectForce = true,
                  onConnect,
                  onConnected,
                  onReconnect,
                  onDisconnect,
                  onDisconnected,
                  onError,
                  timeout = 20,
                  config
                } = {}) {
    this._connectForce = connectForce;

    return new Promise((resolve, reject) => {
      if (this.connected) {
        resolve(this);
        return;
      }

      onConnect?.call(this);
      config?.onConnect?.call(this, this);
      this._onDisconnect = onDisconnect;
      Promise.all([
        this.networkConnectProvide?.connect({config}),
        this.connectBle({
          onConnect, onConnected, onReconnect, onDisconnect, onDisconnected, onError, timeout, config
        })
                .then(() => {
                  resolve(this);

                  this.bleDevice.removeAllListeners("read");
                  const fn = this.sendMessage.bind(this);
                  this.bleDevice.on("read", fn);

                  this.bleDevice.once(DeviceEvent.DISCONNECTED, () => {
                    this.bleDevice.removeListener("read", fn);
                  });
                })
                .catch(e => {
                  reject(WebError.error(e, deviceError.CONNECT_ERROR));
                })]);
    });
  }

  async sendMessage(data) {
    console.log("message", data.action, JSON.stringify(data));
    this._onMessage(data.action, data);
  }

  async _reply(topic, params, payload, {
    reply = false, errorCode, success, complete, fail
  }) {
    await this._publish(topic, params, {
      reply, errorCode, success, complete, fail
    });
  }

  async connectBle({onConnect, onConnected, onDisconnect, onDisconnected, onError, timeout, config} = {}) {
    if (!this.bleDevice || !this._createBleDevice.canConnect?.(config, this.bleDevice)) {
      this.bleDevice = this._createBleDevice.create(config);
    }

    try {
      await this.bleDevice.connect({timeout});
      this.bleDevice.once(DeviceEvent.DISCONNECTED, () => {
        onDisconnected?.call(this);
        this._onMessage(DeviceEvent.DISCONNECTED);
      })
      onConnected?.call(this);
      config?.onConnected?.call(this, this);
    } catch (e) {
      onError?.call(this, e);
      config?.onError?.call(this, this);
      throw WebError.error(e, deviceError.NOT_CONNECT_DEVICE);
    }

    return this.bleDevice;
  }

  get connected() {
    return this.bleDevice ? this.bleDevice.connected : false;
  }

  async _publish(topic, params = [], {
    reply = false, errorCode, id, success, complete, fail
  } = {}) {
    if (this.bleDevice.connected) {
      return this.bleDevice.write({reply, errorCode, action: topic, params: params}).then(() => {
        success?.call(this);
        complete?.call(this);
      });
    } else {
      const e = WebError.code(deviceError.NOT_CONNECT);
      fail?.call(this, e);
      throw e;
    }
  }

  async close(force = true) {
    return this.bleDevice?.disconnect();
  }

  async disconnect() {
    this._onDisconnect?.call(this);
    await this.close();
  }
}


class MqttBleDeviceProtocol extends MqttDeviceProtocol {
  _

  constructor(deviceProvide, deviceInfo, deviceProtocolOption = {}) {
    super(deviceProvide, deviceInfo, deviceProtocolOption);
    this.send = new MqttSend(this, false);

  }

  get deviceConnectProvide() {
    if (this.deviceProvide?.deviceConnectProvide?.networkConnectProvide) {
      return this.deviceProvide.deviceConnectProvide.networkConnectProvide;
    } else {
      return this.deviceProvide?.deviceConnectProvide;
    }
  }

  _createDeviceProtocolBeginSeal(option) {
    return null;
  }

  _createDeviceProtocolOperation() {
    return null;
  }
}

/**
 * @abstract
 */
class BleDeviceProtocol extends DefaultDeviceProtocol {

  /**
   * @type{DeviceConnectProvide}
   */
  networkConnectProvide;

  get connectType() {
    return 1;
  }

  constructor(deviceProvide, deviceInfo, deviceProtocolOption = {}, send) {
    super(deviceProvide, deviceInfo, deviceProtocolOption);

    if (send) {
      this.send = new send(this, this.connectType);
    } else {
      this.send = new BleSend(this, this.connectType);
    }
  }

  get codec() {
    return BleCodec.codec;
  }

  get serviceName() {
    return {
      DEVICE_MATCH: "DEVICEMATCH",
      MAINTAIN: "MAINTAIN",
      USER_LOGOUT: "USERLOGOUT",
      LOCK: "LOCK",
      MOVE: "MOVE",
      REMOTE_LOCK: "REMOTELOCK",
      STICK_OUT_STAMP: "STICKOUTSTAMP",
      STOP_STAMP: "STOPSTAMP",
      STOP_REPLACE: "STOPREPLACE",
      STOP_AINK: "STOPAINK",
      EXAMINE_RESULT: "EXAMINERESULT",
      FINGERPRINT: "FIGRESULT",
      START_STAMP: "STARTSTAMP",
      REPLY_STAMP: "REPLYSTAMP",
      POST_DOCUMENT_FILEID: "POSTDOCUMENTFILEID",
      INKING: "INKING",
      ENVINFO: "ENVINFOV2",

      AINK: "AINK",
      AINK_END: "AINKEND",
      START_REPLACE: "STARTREPLACE",
      END_REPLACE: "ENDREPLACE",
      WIFI_LIST: "WIFILIST",
      WIFI_SETTING: "WIFISETTING",
      WIFI_CONNECT: "WIFICONNECT",
      ENV_CHANGE: "ENVCHANGE",

    }
  }

  async service(name, params = [], {
    success, complete, message, fail, timeout, noReply = false
  } = {}) {
    if (this.connectType !== this.deviceConnectProvide.connectType) {
      console.log("异常发送情况");
      throw WebError.code(deviceError.WRITE_ERROR, "当前发送数据的连接方式与协议对象不一致");
    }

    if (noReply) {
      return this.deviceConnectProvide.publish(name, params, {
        success, complete, message, fail, timeout
      });
    } else {
      return this.deviceConnectProvide.publishAndResult(name, name, params, {
        success, complete, message, fail, timeout
      }).then(data => {
        if (!data.success) {
          throw this.exception({data});
        }

        return data;
      });
    }
  }

  /**
   *
   * @param name
   * @param event
   * @param type
   * @param one
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<String|{code: Number, success: boolean, data: Object}>}
   */
  async listen(name, event = true, {
    one = false, success, complete, message, fail, timeout, topic
  } = {}) {
    let result;
    let index;

    if (one) {
      result = this.deviceConnectProvide.subscribeOne(name, {
        success, complete, message, fail, timeout
      });
    } else {
      index = result = await this.deviceConnectProvide.subscribe(name, {
        success, complete, message, fail
      });
    }

    if (index) {
      if (topic) {
        topic?.call(this, {getTopic: name, index});
      }
    }

    return result;
  }

  async unsubscribe(topics) {
    let clearTopics = [];
    if (topics) {
      if (typeof topics === "string") {
        clearTopics.push(topics);
      } else {
        clearTopics = topics;
      }
    } else {
      clearTopics = this.topics;
    }

    for (const index in clearTopics) {
      await this.deviceConnectProvide.unsubscribe(clearTopics[index], {index});

      if (this.topics[index]) {
        delete this.topics[index];
      }
    }
  }

  async sendPackage({buffer, start, fn, size = 512}) {
    const totalPageSize = Math.ceil(buffer.length / size);
    await start?.call(this, size, totalPageSize);

    for (let i = 1; i <= totalPageSize; i++) {
      const start = (i - 1) * size;
      let end = (i) * size;

      if (end > buffer.length) {
        end = buffer.length;
      }

      await fn?.call(this, i, buffer.slice(start, end), size);
    }
  }

  _createMqttSend() {
    if (this.deviceConnectProvide.networkConnectProvide) {
      this.networkConnectProvide = this.deviceConnectProvide.networkConnectProvide;
      this.mqttBleDeviceProtocol = new MqttBleDeviceProtocol(this.deviceProvide, this.deviceInfo, {
        onUserLogout: (params) => {
          return this.disconnect(params);
        }
      });
      this.mqttSend = this.mqttBleDeviceProtocol.send;
    } else {
      throw WebError.code(deviceError.CRETE_BLE_NOT_NETWORK_CONNECT_PROVIDE);
    }
  }

  get online() {
    return this.mqttBleDeviceProtocol.online;
  }

  async _connect({success, complete, message, fail, timeout = 20} = {}) {
    this._createMqttSend();
    this.emit(DeviceEvent.BEFORE_USER_LOGIN);
    this.deviceConnectProvide.codec = this.codec;

    await this._userLoginSend(timeout);

    await this.service(this.serviceName.DEVICE_MATCH, [this.deviceInfo.userName, this.deviceInfo.staffId], {
      success, complete, message, fail, timeout
    });

    this.emit(DeviceEvent.USER_LOGIN);
  }

  async _userLoginSend() {
    const params = {
      model: this.connectType,
      sessionId: this.deviceInfo.sessionId,
      deviceType: config.deviceType,
      number: this.deviceInfo.number,
      gps: config.gps
    }
    if (this.request) {
      return this.request?.userLoginSend(params);
    } else {
      return await service.post(url.userLogin, params).then(result => {
        return result.data
      });
    }
  }

  async _getSignSend() {
    if (this.request) {
      return this.request?.getSignSend(this.deviceInfo.number);
    } else {
      return await service.post(url.getSign, {
        deviceNumber: this.deviceInfo.number
      }).then(result => {
        return result.data
      });
    }
  }

  async _propertySyncSend(params) {
    const request = {
      number: this.deviceInfo.number,
      energyType: params.energyType,
      energy: params.energy,
      bleMac: params.bleMac,
      plank: params.plank,
      systemVersion: params.systemVersion,
      version: params.version
    }
    if (this.request) {
      return this.request?.propertySyncSend(request);
    } else {
      const data = {
        ...params
      }
      if (!data.number) data.number = this.deviceInfo.number
      return await service.put(url.propertySync, data);
    }
  }

  async _userLogoutSend() {
    if (this.request) {
      return this.request?.userLogoutSend();
    } else {
      return await service.put(url.userLogout, {
        number: this.deviceInfo.number,
        sessionId: this.deviceInfo.sessionId
      })
    }
  }

  async _documentPrivilegeUploadSend(dataWithNumber) {
    if (this.request) {
      return this.request?.documentPrivilegeUploadSend(dataWithNumber);
    } else {
      return service.post(request.documentPrivilegeUpload, dataWithNumber)
    }
  }

  fail(complete, fail) {
    return (e) => {
      fail?.call(this, e);
      complete?.call(this, null, e);
    };
  }

  async _disconnect({type, logout, success, complete, message, fail, timeout} = {}) {
    try {
      await this._userLogoutSend(timeout);
      if (this.deviceConnectProvide.connected && this.deviceProtocolBeginSeal) {
        await this.deviceProtocolBeginSeal.endSeal({force: true, type, logout});
      }

      await this.send.unsubscribe();
      await success?.call(this);
      complete?.call(this);
    } catch (e) {
      complete?.call(this, null, e);
    }
  }

  async _fingerprintImportSend() {
    if (this.request) {
      return this.request?.fingerprintImportSend(this.deviceInfo.number);
    } else {
      return await service.getFrom(request.fingerprintImport, {
        number: this.deviceInfo.number
      });
    }
  }

  async sync() {
    try {
      await this.send.clearKeepAlive?.();
      await this.setDeviceConfig({config: this.deviceInfo});
      await this.precord();
      const fingerprintImport = await this._fingerprintImportSend();
      await this._sync(fingerprintImport)
    } finally {
      await this.send.createKeepAlive?.();
    }
  }

  /**
   * 同步指纹等数据
   * @return {Promise<{code,data,success}>} 无返回值
   * @private
   */
  async _sync(fingerprintImport) {
    let positions = this.deviceInfo.position ? this.deviceInfo.position : [];

    if (fingerprintImport.data && fingerprintImport.data.length === 0) {
      this.emit(DeviceEvent.SYNC_STATUS, {progress: 1, type: 1, data: fingerprintImport});
    } else {
      this.emit(DeviceEvent.SYNC_STATUS, {progress: 0, type: 1, data: fingerprintImport});

      let error = false;

      for (const i in fingerprintImport.data) {
        const type = fingerprintImport.data[i].type;
        const auth = fingerprintImport.data[i].auth;
        const position = fingerprintImport.data[i].position;
        const staffId = fingerprintImport.data[i].staffId;
        const featureString = fingerprintImport.data[i].featureString;

        if (type === 1) {
          positions.push(position);
        } else if (type === 2) {
          positions = positions.filter(item => item !== type);
        } else if (type === 3) {
          positions = [];
        }

        await this.figsync({type, auth, position, staffId, featureString}).then(data => {
          this.emit(DeviceEvent.SYNC_STATUS, {
            progress: i / fingerprintImport.data.length,
            type: 1,
            data: fingerprintImport
          });
          return data;
        }).catch(e => {
          console.error("同步指纹失败", e);
          error = true;
          if ((i + 1) !== fingerprintImport.data.length) {
            this.emit(DeviceEvent.SYNC_STATUS, {
              progress: i / fingerprintImport.data.length,
              type: 1,
              data: fingerprintImport,
              error: e
            });
          }
          return null;
        })
      }

      this.emit(DeviceEvent.SYNC_STATUS, {progress: 1, type: 1, data: fingerprintImport});
    }

    this.deviceInfo.position = positions;

    return fingerprintImport;
  }

  async figsync({type, position, auth, featureString, success, complete, message, fail, timeout} = {}) {

  }

  async _fingerprintImportStateSend({type, position, auth, featureString}) {
    const params = {
      number: this.deviceInfo.number,
      type, position
    }
    if (this.request) {
      return this.request?.fingerprintImportStateSend(params);
    } else {
      return await service.put(url.fingerprintImportState, {
        number: this.deviceInfo.number,
        type, position
      })
    }
  }
}

class BleSend {
  /**
   * @type {BleDeviceProtocol}
   */
  deviceProtocol;
  topics = {};
  connectType = 1;
  _listenTopic = {};
  isKeepAlive = true;


  constructor(deviceProtocol) {
    this.deviceProtocol = deviceProtocol;

    setTimeout(() => {
      this.init().finally();
    }, 200);
  }

  async init() {
    if (this.deviceProtocol.connected || (this.deviceProtocol.deviceInfo.property && this.deviceProtocol.deviceConnectProvide.connected)) {
      await this.onDisconnect();
    } else {
      const onConnect = ({connectType}) => {
        if (connectType === this.connectType) {
          if (!this._init) {
            this.onDisconnect();
            this._init = true;
          }

          this.deviceProtocol.removeListener(DeviceEvent.CONNECTED, onConnect);
        }
      };
      this.deviceProtocol.on(DeviceEvent.CONNECTED, onConnect)
    }
  }

  async onDisconnect() {
    await this.deviceProtocol.on(DeviceEvent.USER_LOGIN, () => {
      this.createKeepAlive();
    });

    await this.deviceProtocol.on(DeviceEvent.DISCONNECTED,
            async (data) => {
              this.deviceProtocol.emit(DeviceEvent.OFFLINE, data);
              await this.disconnect();
            }
    );

    await this.listen(this.deviceProtocol.serviceName.USER_LOGOUT, {
      cacheTopic: this._listenTopic,
      message: async ({params}) => {
        const type = parseInt(params[1]);
        await this.deviceProtocol.disconnect({force: true, logout: true, type: type ? 7 + type : 3, connectType: 1});
      }
    })
  }

  async disconnect(clear = false) {
    if (this.clearKeepAlive) {
      this.clearKeepAlive();

      if (!clear) {
        await this.deviceProtocol.disconnect({force: true, logout: true, type: 3, connectType: 1});
        this.deviceProtocol.online = false;
      }
    }
  }

  createKeepAlive() {
    if (!this.isKeepAlive) {
      return
    }

    this.keepAliveCount = 0;

    if (!this.keepAliveMethod) {
      const clear = async (type = 4) => {
        await this.clearKeepAlive();
        await this.deviceProtocol.disconnect({force: true, type: type, connectType: 1});
      };


      this.deviceProtocol.once(DeviceEvent.OFFLINE, () => {
        this.clearKeepAlive?.();
      })

      this.clearKeepAlive = () => {
        clearInterval(this.keepAliveMethod, true);
        this.keepAliveMethod = null;
      };

      this.deviceProtocol.once(DeviceEvent.DISCONNECTED, clear);
      const keepalive = async () => {
        if (!this.deviceProtocol.deviceConnectProvide?.connected) {
          return;
        }

        if (this.keepAliveCount > 2) {
          await clear(7);
          return;
        }

        try {
          this.keepAliveCount = this.keepAliveCount ? this.keepAliveCount + 1 : 1;
          const result = await this.keepAlive();

          if (!result.success) {
            await clear();
          }

          this.keepAliveCount = 0;
        } catch (e) {
          console.warn("发送心跳异常", e);
        }
      };

      keepalive();
      this.keepAliveMethod = setInterval(keepalive, 30 * 1000, true);
    }
  }

  /**
   *
   * @param name
   * @param params
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @param noReply
   * @return {Promise<*>}
   */
  async service(name, params = [], {
    success, complete, message, fail, timeout, noReply
  } = {}) {
    this.sendTime = new Date().getTime();
    return this.deviceProtocol.service(name, params, {
      success, complete, message, fail, timeout, noReply
    });
  }

  /**
   *
   * @param name
   * @param event
   * @param one
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<String|{code: Number, success: boolean, data: Object}>}
   */
  async listen(name, {
    one = false, cacheTopic = true, success, complete, message, fail, timeout
  } = {}) {
    return this.deviceProtocol.listen(name, true, {
      one, success, complete, message, fail, timeout, topic: ({getTopic, index}) => {
        if (cacheTopic) {
          if (cacheTopic === true && index && !this.topics[index]) {
            this.topics[index] = getTopic;
          }

          if (cacheTopic !== true) {
            cacheTopic[index] = getTopic;
          }
        }
      }
    });
  }

  async unsubscribe(topics = undefined) {
    let clearTopics = [];
    if (topics) {
      if (typeof topics === "string") {
        clearTopics.push(topics);
      } else {
        clearTopics = topics;
      }
    } else {
      clearTopics = this.topics;
    }

    for (const index in clearTopics) {
      await this.deviceProtocol.deviceConnectProvide.unsubscribe(clearTopics[index], {index});

      if (this.topics[index]) {
        delete this.topics[index];
      }
    }
  }

  async keepAlive({success, complete, message, fail, timeout} = {}) {
    return this.service(this.deviceProtocol.serviceName.MAINTAIN, [], {success, complete, message, fail, timeout});
  }

  async remoteLock({documentId, lock, success, complete, message, fail, timeout} = {}) {
    return this.service(this.deviceProtocol.serviceName.REMOTE_LOCK, [documentId, lock], {
      success, complete, message, fail, timeout, noReply: true
    });
  }

  async cancelOperation({type, success, complete, message, fail, timeout} = {}) {
    // if (type === 1 && this.deviceProtocol.deviceInfo.leftTimes <= 0 && this.deviceProtocol.deviceInfo.remote !== DeviceBeginSealType.remote) {
    //   return;
    // }
    // const noReply = type === 1 && this.deviceProtocol.deviceInfo.leftTimes <= 0 && this.deviceProtocol.deviceInfo.remote === DeviceBeginSealType.remote

    const noReply = true;

    switch (type) {
      case 1:
        return this.service(this.deviceProtocol.serviceName.STOP_STAMP, [], {
          success, complete, message, fail, timeout, noReply
        });
      case 2:
        return this.service(this.deviceProtocol.serviceName.STOP_REPLACE, [], {
          success, complete, message, fail, timeout
        });
      case 3:
        return null;
      case 4:
        return this.service(this.deviceProtocol.serviceName.STOP_AINK, [], {success, complete, message, fail, timeout});
    }
  }
}

class BleDeviceProtocolBeginSeal extends DefaultDeviceProtocolBeginSeal {

  /**
   *
   * @param deviceInfo
   * @param deviceProtocol{BleDeviceProtocol}
   * @param option
   */
  constructor(deviceInfo, deviceProtocol, option) {
    super(deviceInfo, deviceProtocol, option);

    /**
     *
     * @type {BleSend}
     */
    this.send = deviceProtocol.send;

    /**
     *
     * @type {MqttSend}
     */
    this.mqttSend = deviceProtocol.mqttSend;
  }

  async _examineResult(documentRemoteId, state, {mode, success, complete, message, fail, timeout} = {}) {
    if (this.deviceInfo.modelType === DeviceType.a) {
      return this.send.service(this.deviceProtocol.serviceName.EXAMINE_RESULT, [documentRemoteId, state], {
        success, complete, message, fail, timeout
      });
    } else {
      return this.send.service(this.deviceProtocol.serviceName.EXAMINE_RESULT, [this.deviceInfo.documentId, state, mode], {
        success, complete, message, fail, timeout, noReply: state === 1
      });
    }
  }

  async _fingerprintIdentify({success, complete, message, fail, timeout = -1} = {}) {
    return new Promise((resolve, reject) => {
      this.send.listen(this.deviceProtocol.serviceName.FINGERPRINT, {
        success, complete, timeout, message: msg => {
          const data = {
            params: {
              documentId: msg.params[1], sealid: msg.params[2], state: parseInt(msg.params[3])
            }
          };
          let result;
          if (data?.params?.sealid == this.option.sealId?.toString() && data?.params?.documentId == this.option.documentId?.toString()) {
            this.deviceProtocol.emit(DeviceEvent.FINGERPRINT_IDENTIFY, result = {
              documentId: this.option.documentId,
              sealId: this.option.sealId,
              state: data.params?.state,
              success: data.params?.state === 1
            });

            message?.call(this, result);

            if (data.params?.state === 1) {
              resolve(result);
            }
          }
        }, fail: (err) => {
          fail?.call(this, err);
          reject(err);
        }
      });
    });
  }

  async _beginSeal({authentication = true, success, complete, message, fail, timeout} = {}) {
    if (this.option.equipmentRec === 1 || this.option.hasCover || this.deviceInfo.sealAuthentication === 1) {
      throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT)
    }

    return this.send.service(this.deviceProtocol.serviceName.START_STAMP, [
      this.option.documentId,
      this.option.sealId,
      this.option.leftTimes,
      this.option.position ? this.option.position.map(s => s.toString().padLeft(3, "0")).join("") : "000",
      this.remote,
      authentication ? this.option.authentication : 2,
      this.option.pictureType,
      this.option.infrared,
      this.deviceInfo.operationId,
    ], {
      success, complete, message, fail, timeout
    });
  }

  async _sealStretch({out = true, urgent, success, complete, message, fail, timeout} = {}) {
    if (out) {
      return this.send.service(this.deviceProtocol.serviceName.STICK_OUT_STAMP, [0], {
        success, complete, message, fail, timeout
      })
    }

    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT);
  }

  async _remoteLock({documentId, lock, success, complete, message, fail, timeout} = {}) {
    return this.send.remoteLock({
      documentId, lock, success, complete, message, fail, timeout
    });
  }

  async _endSeal({success, complete, message, fail, timeout} = {}) {
    return this.send.cancelOperation({
      type: 1, success, complete, message, fail, timeout
    }).then(data => {
      this.send.disconnect(true);
      this.send.unsubscribe();
      this.mqttSend?.unsubscribe();

      return data;
    });
  }

  async _onSealEvent({success, complete, message, fail, timeout} = {}) {
    try {
      await this.send.listen(this.deviceProtocol.serviceName.REPLY_STAMP, {
        message: async msg => {
          console.log("监听到盖印数据", msg);
          const data = {
            // documentId~position~sealId~leftTimes~locateMode~gps~sealTime
            documentId: msg.params[1],
            position: msg.params[2],
            sealId: msg.params[3],
            leftTimes: msg.params[4],
            sealTime: msg.params[5],
            staffId: this.deviceInfo.staffId,
            coverId: this.deviceInfo.coverId
          };

          this.option.leftTimes = data.leftTimes;
          this.deviceInfo.leftTimes = data.leftTimes;

          this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 1});

          const result = await this._stampDataUploadSend(data);

          this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 2});
          data.documentFileId = result.data.documentFileId;
          this.deviceProtocol.emit(DeviceEvent.DOCUMENT_STATE_UPLOADING, data);

          setTimeout(() => {
            if (this.remote !== 3) {
              this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 3});
            }
          }, 2000)

          this.send.service(this.deviceProtocol.serviceName.POST_DOCUMENT_FILEID, [result.data.documentFileId]).catch(e => {
            console.warn("传输documentFileId失败");
          });

          await this.mqttSend.listen("stop_privilege_seal", true, {
            message: data => {
              if (this.option.documentId === data.data?.documentId && data.data?.staffIds?.find(staffId => staffId === this.option.staffId)) {
                this.deviceProtocol.emit(DeviceEvent.STOP_PRIVILEGE_SEAL, data);
                this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 5});
                this.endSeal({force: true, type: 5});
              }

              message?.call(this, data);
            }, fail, timeout
          });

          message?.call(this, data);

          if (this._mode === 1 && this._context.isExamine) {
            setTimeout(() => {
              this.contextReset(true);
            }, 1000);
          }
        }, fail, timeout
      });


      this.mqttSend.listen("document_file_uploadMsg_event", false, {
        message: data => {
          console.log("监听到盖印数据", data);

          if (data.params.documentId == this.deviceInfo.documentId && data.params.staffId == this.deviceInfo.staffId) {
            if (this.option.currStartLeftTimes < data.params.leftTimes) {
              console.warn(`上报离线图片数据, 开始: ${this.option.currStartLeftTimes}, 现数据：${this.deviceInfo.leftTimes}, 原数据：${data.params.leftTimes}`)
              return;
            }
            this.deviceProtocol.emit(DeviceEvent.DOCUMENT_FILE_UPLOAD, data);
          }
        }
      });

      await success?.call(this);
      complete?.call(this);
    } catch (e) {
      complete?.call(this, null, e);
      throw WebError.code(deviceError.SYSTEM_ERROR, {
        error: e
      });
    }
  }

  async _equipmentMoveSend({documentRemoteId}) {
    if (this.deviceProtocol.request) {
      await this.deviceProtocol.request?.equipmentMoveSend({documentRemoteId, state: 4});
    } else {
      await service.put(url.equipmentMove, {documentRemoteId, state: 4});
    }
  }

  async _stampDataUploadSend(data) {
    if (this.deviceProtocol.request) {
      return this.deviceProtocol.request?.stampDataUploadSend(data);
    } else {
      return await service.post(url.stmpDataUpload, {
        operationId: this.deviceInfo.operationId,
        stage: 1,
        documentId: data.documentId,
        position: data.position,
        sealId: data.sealId,
        leftTimes: data.leftTimes,
        gps: config.gps,
        sealTime: data.sealTime,
        number: this.deviceInfo.number,
        staffId: data.staffId,
        coverId: data.coverId
      })
    }
  }

  async _stampDataWarningSend(data) {
    const params = {
      businessType: data.businessType,
      documentId: data.documentId,
      sealId: data.sealId,
      staffId: data.staffId,
      lon: data.lon,
      lat: data.lat,
      warningTime: data.warningTime
    };
    if (this.deviceProtocol.request) {
      return this.deviceProtocol.request?.stampDataWarningSend(params);
    } else {
      return await service.post(url.stampdataWarning, params);
    }
  }

  async _equipmentLock({
                         sendEquipmentLock, success, complete, message, fail, timeout = -1
                       } = {}) {
    let isStart = true;
    const data = await sendEquipmentLock?.call(this);

    const documentRemoteId = data.documentRemoteId;


    await this.send.service(this.deviceProtocol.serviceName.LOCK, [documentRemoteId]);
    let subscribeIndex;
    let clear = false;
    const moveEvent = this.deviceProtocol.serviceName.MOVE;

    const onDocumentStateUploading = params => {
      params.documentRemoteId = documentRemoteId;
      this.deviceProtocol.emit(DeviceEvent.DOCUMENT_REMOTE_STATE_UPLOADING, params);
    };

    /**
     *
     * @param type 1:正常结束  2:移动取消  3:app取消 4远程拒绝
     * @return {Promise<void>}
     */
    const clearMoveEvent = async (type = 3) => {
      isStart = false;
      if (subscribeIndex && !clear) {
        clear = true;
        this.deviceProtocol.deviceConnectProvide.unsubscribe(moveEvent, {index: subscribeIndex});
        if (type === 2) {
          this.deviceProtocol.emit(DeviceEvent.EQUIPMENT_MOVE, {documentRemoteId});
        } else if (type === 3) {
          await this.examineResult(documentRemoteId, 2, {mode: 1}).catch(e => {
            return null;
          });

          //this._remoteUnlockSend(documentRemoteId);
        }

        if (type !== 1) {
          this.deviceProtocol.removeListener(DeviceEvent.DOCUMENT_STATE_UPLOADING, onDocumentStateUploading);
        }

        this.deviceProtocol.emit(DeviceEvent.EQUIPMENT_UNLOCK, {documentRemoteId, type: type});
      }
    };

    this.deviceProtocol.deviceConnectProvide.subscribeOne(moveEvent, {
      success: index => {
        subscribeIndex = index;
      }, filter: params => {
        return documentRemoteId === params[0];
      }, message: (params, reply) => {
        this._equipmentMoveSend({documentRemoteId});
        clearMoveEvent(2);
      }, timeout
    }).finally();

    this.once(DeviceEvent.DOCUMENT_STATE_UPLOADING, onDocumentStateUploading);

    const listen = async () => {
      while (isStart) {
        const data = await this._remoteResultSend(documentRemoteId);

        switch (data) {
          case 1:
          case 2:
            await this.examineResult(documentRemoteId, data, {mode: 1}).catch(e => {
              if (data === 2) {
                return null;
              }

              throw e;
            });

            await clearMoveEvent(data === 1 ? 1 : 4);
            break;
          case 3:
            await clearMoveEvent(3);
        }

        await this.delay(0.5);
      }
    }


    return {
      documentRemoteId, cancel: clearMoveEvent, listen: listen
    };
  }

  async _remoteUnlockSend(documentRemoteId) {
    if (this.deviceProtocol.request) {
      return this.deviceProtocol.request?.remoteUnlockSend({
        number: this.deviceInfo.number, documentRemoteId: documentRemoteId
      }).catch(e => {
        console.warn("远程用印取消失败", e);
        return null;
      });
    } else {
      return service.putForm(url.remoteUnlock, {
        number: this.deviceInfo.number, documentRemoteId: documentRemoteId
      }).catch(e => {
        console.warn("远程用印取消失败", e);
        return null;
      })
    }
  }

  async _remoteResultSend(documentRemoteId) {
    if (this.deviceProtocol.request) {
      return this.deviceProtocol.request?.remoteResultSend(
              documentRemoteId
      ).catch(e => {
        console.warn("远程用印轮训失败", e);
      });
    } else {
      const {data} = await service.get(request.remoteResult.format(documentRemoteId)).catch(e => {
        console.warn("远程用印轮训失败", e);
      });

      return data;
    }
  }

  async delay(seconds = 0) {
    return new Promise(resolve => setTimeout(resolve, seconds * 1000));
  }
}


class BleDeviceProtocolOperation extends DefaultDeviceProtocolOperation {

  constructor(deviceInfo, deviceProtocol) {
    super(deviceInfo, deviceProtocol);
  }


  /**
   *
   * @type {BleSend}
   */
  get send() {
    return this.deviceProtocol.send;
  }

  /**
   *
   * @type {MqttSend}
   */
  get mqttSend() {
    return this.deviceProtocol.mqttSend;
  }

  async _envinfo({success, complete, message, fail, timeout} = {}) {
    const r = await this.send.service(this.deviceProtocol.serviceName.ENVINFO, [], {
      success,
      complete,
      message,
      fail,
      timeout
    });
//     这里3 表示私有化参数（其他的1表示saas环境，0表示测试环境 2表示验证环境）
// 这里http://testapiyda.com表示配置的url地址信息
// 这里116.289.255.230:1889表示IOT配置地址信息
    return {env: r.params[1],api: r.params[2],iot: r.params[3]}
  }

  async _withInk({success, complete, message, fail, timeout} = {}) {
    return this.send.service(this.deviceProtocol.serviceName.INKING, [], {
      noReply: true,
      success,
      complete,
      message,
      fail,
      timeout
    });
  }

  async _beginInk({success, complete, message, fail, timeout} = {}) {
    return this.send.service(this.deviceProtocol.serviceName.AINK, [], {success, complete, message, fail, timeout});
  }

  async _beginInkResult({success, complete, message, fail, timeout} = {}) {
    return this.send.service(this.deviceProtocol.serviceName.AINK_END, [], {
      success, complete, message, fail, timeout
    }).then(async data => {
      // await this._beginInkResultSend();
      return data;
    });
  }

  async _beginInkResultSend({status, msg = "", success, complete, message, fail, timeout} = {}) {
    return this.mqttSend.service("bt_begin_ink_result", {
      status, msg, success, complete, message, fail, timeout
    });
  }

  async _cancelOperation({type, success, complete, message, fail, timeout} = {}) {
    if (type === 1 && this.deviceProtocol.deviceInfo.leftTimes <= 0) {
      return;
    }

    return this.send.cancelOperation({
      type, success, complete, message, fail, timeout
    });
  }

  async _changeSeal({sealId, sealName, motion, success, complete, message, fail, timeout} = {}) {
    return this.send.service(this.deviceProtocol.serviceName.START_REPLACE, [sealId, sealName, motion], {
      success, complete, message, fail, timeout
    });
  }

  async _changeSealFinish({sealId, sealName, motion, success, complete, message, fail, timeout} = {}) {
    return this.send.service(this.deviceProtocol.serviceName.END_REPLACE, [sealId, sealName, motion], {
      success, complete, message, fail, timeout
    }).then(async data => {
      await this._changeSealFinishSend({
        sealId, sealName, motion, status: 1
      });
      return data;
    });
  }

  async _firmwareUpdateSend({version}) {
    const params = {
      number: this.deviceInfo.number,
      step: 3,
      version: version
    }
    if (this.deviceProtocol.request) {
      return this.deviceProtocol.request?.firmwareUpdateSend(params);
    } else {
      return await service.put(url.firmwareUpdate, params)
    }
  }

  async _changeSealFinishSend({sealId, motion, status, success, complete, message, fail, timeout} = {}) {
    const params = {
      number: this.deviceInfo.number, sealId, motion
    }
    if (this.deviceProtocol.request) {
      return this.deviceProtocol.request?.changeSealFinishSend(params);
    } else {
      return service.put(request.changeSealEnd, params)
    }
  }

  async _remoteLock({documentId, lock, success, complete, message, fail, timeout} = {}) {
    return this.send.remoteLock({documentId, lock, success, complete, message, fail, timeout});
  }

  async _deviceMaintain({success, complete, message, fail, timeout} = {}) {
    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT);
  }

  async _fingerprintEntering({staffId, valid, auth, success, complete, message, fail, timeout} = {}) {
    const result = await this._fingerprintEnteringSend({staffId, valid, auth});

    await this.send.service(this.deviceProtocol.serviceName.FIG, [result.type, result.position, result.auth], {
      success,
      complete,
      message,
      fail,
      timeout
    });

    const _onFingerprint = await this._onFingerprint(result.type, result.position, result.auth, staffId);
    return {
      type: result.type, position: result.position, auth: result.auth, cancel: () => {
        return _onFingerprint?.cancel();
      }
    };
  }

  async _fingerprintEnteringSend({staffId, valid}) {
    const params = {
      number: this.deviceInfo.number,
      staffId: staffId,
      remote: 2,
      valid
    }
    if (this.deviceProtocol.request) {
      return this.deviceProtocol.request?.fingerprintEnteringSend(params);
    } else {
      return service.put(request.fingerprint, {
        number: this.deviceInfo.number,
        staffId: staffId,
        remote: 2
      }).then(result => {
        const {type, position, auth} = result.data;

        return {type, position, auth};
      })
    }
  }

  async _fingerprintRemove({staffId, type, success, complete, message, fail, timeout} = {}) {
    const result = await this._fingerprintRemoveSend(staffId);

    if (type === 2) {
      for (const i in result) {
        const position = result[i];
        await this.send.service(this.deviceProtocol.serviceName.FIG, [2, position], {
          noReply: true,
          success,
          complete,
          message,
          fail,
          timeout
        });

        await this._fingerprintRemoveFinishSend({staffId, type, position});
      }
    } else if (type === 3) {
      await this.send.service(this.deviceProtocol.serviceName.FIG, [3, 0, 0], {
        noReply: true,
        success,
        complete,
        message,
        fail,
        timeout
      });

      await this._fingerprintClearFinishSend({staffId, type});
    }

    return {
      type,
      position: result.data
    }
  }

  async _fingerprintRemoveSend({staffId}) {
    const params = {
      staffId: staffId,
      number: this.deviceInfo.number
    };
    if (this.deviceProtocol.request) {
      return this.deviceProtocol.request?.fingerprintRemoveSend(params);
    } else {
      return service.getFrom(request.fingerprintRemoveBle, params).data;
    }
  }

  async _fingerprintRemoveFinishSend({staffId, type, position}) {
    const params = {
      number: this.deviceInfo.number,
      staffId,
      type, position
    }

    if (this.deviceProtocol.request) {
      return this.deviceProtocol.request?.fingerprintRemoveFinishSend(params);
    } else {
      return await service.put(url.fingerprintImportState, params)
    }
  }

  async _fingerprintClearFinishSend({staffId, type}) {
    const params = {
      number: this.deviceInfo.number,
      staffId,
      type
    };
    if (this.deviceProtocol.request) {
      return this.deviceProtocol.request?.fingerprintClearFinishSend(params);
    } else {
      return await service.put(url.fingerprintImportState, params)
    }
  }

  async _onFingerprint(type, position, auth, staffId) {
    let buffer = "";
    const clear = {};

    const totalSize = 12;
    await this.send.listen("GETFEATURE", {

      cacheTopic: clear,
      message: ({params}) => {
        if (params[0] === "OK") {
          const feature = params[2];
          const pageIndex = parseInt(params[1]);

          buffer += feature;

          if (pageIndex === totalSize) {
            this._fingerprintFinishSend({type, position, auth, buffer, staffId}).then((data) => {
              this.deviceProtocol.emit(DeviceEvent.FINGERPRINT_FINISH, data);
            }).finally(() => {
              this.send.unsubscribe(clear);
            })
          }
        }
      }
    })
    await this.send.listen("FIGSTEP", {
      cacheTopic: clear,
      message: (data) => {
        const params = data.params;
        const step = parseInt(params[1]);
        this.deviceProtocol.emit(DeviceEvent.FINGERPRINT_TYPE, {step, data, type, position, auth});
        if (step === 3) {
          this.send.service("GETFEATURE", [position]);
        }
      }
    })

    return {
      cancel: () => {
        this.send.unsubscribe(clear);
        return this.send.cancelOperation({type: 3});
      }
    }
  }

  async _fingerprintFinishSend({type, position, auth = 0, buffer, staffId}) {
    const file = new File([buffer], "feature.txt");
    const formData = new FormData();
    formData.append("file", file);
    const params = {
      file: file,
      number: this.deviceInfo.number,
      position,
      status: 1,
      staffId,
    }

    if (this.deviceProtocol.request) {
      return this.deviceProtocol.request?.fingerprintFinishSend(params);
    } else {
      try {
        const re = await service.postForm(request.fileUpload, formData).then((data) => {
        if (!data.success) {
          return data;
        }
        return service.put(request.fingerprintOperateState, {
          number: this.deviceInfo.number,
          position,
          feature: data.data.id,
          status: 1,
        })
      });
      return re
      } catch (error) {
        console.error('fileUpload error:',error)
        return error.data
      }
    }
  }

  async firmwareUpgrade({success, complete, message, fail, timeout} = {}) {
    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT_CHANGE, {
      data: {
        curr: 1, change: 2
      }
    })
  }

  async _firmwareUpgrade({data, success, complete, message, fail, timeout}) {
    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT_CHANGE, {
      data: {
        curr: 1, change: 2
      }
    })
  }

  async _singing({state, success, complete, message, fail, timeout}) {
    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT);
  }

  async _wifiSetting({type, save, ssid, password, success, complete, message, fail, timeout}) {
    let list = []
    save != undefined ? list = [type, save, ssid, password] : list = [type, ssid, password]
    return this.send.service(this.deviceProtocol.serviceName.WIFI_SETTING, list, {
      success, complete, message, fail, timeout
    }).then(data => {
      return {
        status: parseInt(data.params[1]),
        message: data.params[2]
      }
    });
  }

  async _envChange({api, iot, type, clear, success, complete, message, fail, timeout} = {}) {
    return this.send.service(this.deviceProtocol.serviceName.ENV_CHANGE, [api, iot, type, clear], {
      success, complete, message, fail, timeout
    });
  }

  async _connectChange({usbMode, success, complete, message, fail, timeout} = {}) {
    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT)
  }

  async _wifiList({onWifi, success, complete, message, fail, timeout}) {
    const wifi = [];
    const topic = {};
    const index = await this.send.listen(this.deviceProtocol.serviceName.WIFI_LIST, {
      cacheTopic: topic, message: ({params}) => {
        const data = {
          rssi: parseInt(params[1]),
          flags: parseInt(params[2]),
          ssid: params[3],
          link: parseInt(params[4]),
          save: parseInt(params[5])
        }

        if (!wifi.find(w => w.ssid === data.ssid)) {
          wifi.push(data);
          onWifi?.call(this, wifi);
        }
      }
    });

    this.send.service(this.deviceProtocol.serviceName.WIFI_LIST, [], {
      success, complete, message, fail, timeout
    }).then();

    return {
      setting: ({type, save, ssid, password, success, complete, message, fail, timeout}) => {
        this.send.unsubscribe(topic);
        return this.wifiSetting({type, save, ssid, password, success, complete, message, fail, timeout});
      }, cancel: () => {
        return this.send.unsubscribe(topic);
      }, onWifi: true
    };
  }

  async release() {
    return this.send.unsubscribe();
  }
}

export {
  BleDeviceConnectProvide,
  BleDeviceProtocolBeginSeal,
  BleDeviceProtocol,
  BleDeviceProtocolOperation,
  MqttBleDeviceProtocol,
  BleDevice,
  BleSend,
  BleCodec
};